import { AuthorityService } from '@/types/enums/AuthorityService';
import type { BatchSignDocument } from '@/types/deepsign/BatchSignDocument';
import type { OverviewDocument } from '@/stores/overview';
import { SignatureMode } from '@/types/enums/SignatureMode';
import { Jurisdiction } from '@/types/enums/Jurisdiction';
import { SignStatus } from '@/types/enums/SignStatus';
import { assign, fromPromise, setup } from 'xstate';

type PossibleAuthorityServiceItem = { key: AuthorityService };

export interface BatchProcessContext {
  documents: OverviewDocument[];
  signeeIssues: unknown[];
  signed_documents: BatchSignDocument[];
  error?: unknown;
  authority_service?: AuthorityService;
  possible_authority_service?: PossibleAuthorityServiceItem[];
  batch_sign_response?: unknown;
  completed_time?: unknown;
}

export type BatchProcessEvent =
  | { type: 'BEGIN_CHECKING' }
  | { type: 'CANCEL_CHECKING' }
  | { type: 'SELECT_AUTHORITY_SERVICE'; authority_service: AuthorityService }
  | { type: 'BEGIN_SIGNING' }
  | { type: 'CONTINUE_SIGNING' }
  | { type: 'UNAVAILABLE_SERVICE' }
  | { type: 'REMOVE_DOCUMENT_WITH_AUTOGRAPH_REQUIRED' }
  | { type: 'SET_SIGN_RESPONSE'; batch_sign_response: unknown }
  | { type: 'COMPLETE_SIGNING'; signed_documents: BatchSignDocument[] }
  | { type: 'REMOVE_DOCUMENT'; documentId: string }
  | { type: 'OPEN_PENDING' }
  | { type: 'START_POLLING' }
  | { type: 'RETRY' };

export type BatchProcessInput = {
  documents: OverviewDocument[];
  signed_documents?: BatchSignDocument[];
  error?: unknown;
  authority_service?: AuthorityService;
  possible_authority_service?: PossibleAuthorityServiceItem[];
  batch_sign_response?: unknown;
  completed_time?: unknown;
};

type ActorErrorPayload = { messageId?: string; documentId?: string };

type CheckAuthActorInput = {
  documents: OverviewDocument[];
  authority_service?: AuthorityService;
};
type CheckAuthActorOutput = {
  next: 'identify' | 'beginSigning';
  unavailable: boolean;
};

type SignBatchActorInput = {
  documents: OverviewDocument[];
  authority_service?: AuthorityService;
};
type SignBatchActorOutput = { batch_sign_response: unknown };

function isPayload(v: unknown): v is ActorErrorPayload {
  if (!v || typeof v !== 'object') return false;
  return 'messageId' in v || 'documentId' in v;
}

function getErrorPayload(event: unknown): ActorErrorPayload | undefined {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const e = event as any;

  const candidates = [
    e?.error?.response?.data,
    e?.error?.data,
    e?.error,
    e?.data,
    e
  ];

  for (const c of candidates) {
    if (!c) continue;
    if (isPayload(c)) return c;
    if (typeof c === 'object' && c.data && isPayload(c.data)) return c.data;
  }

  return undefined;
}

function calcPossibleAuthorityServices(docs: OverviewDocument[]) {
  const allEidasAdvanced =
    docs.length > 0 &&
    docs.every((d) => {
      if (!d) return false;
      return (
        d.signatureMode === SignatureMode.ADVANCED &&
        d.jurisdiction === Jurisdiction.EIDAS
      );
    });

  if (allEidasAdvanced) {
    return {
      possible: [
        { key: AuthorityService.DEEP_ID }
      ] as PossibleAuthorityServiceItem[],
      enforced: AuthorityService.DEEP_ID
    };
  }

  const defined = docs
    .map((d) => d?.requiredAuthorityService)
    .filter((s): s is AuthorityService => s != null);

  const unique = Array.from(new Set(defined));

  if (unique.length === 0) {
    return {
      possible: [
        { key: AuthorityService.DEEP_ID },
        { key: AuthorityService.MOBILE_ID }
      ] as PossibleAuthorityServiceItem[],
      enforced: undefined as AuthorityService | undefined
    };
  }

  if (unique.length === 1) {
    return {
      possible: [{ key: unique[0] }] as PossibleAuthorityServiceItem[],
      enforced: unique[0]
    };
  }

  return {
    possible: unique.map((s) => ({ key: s })) as PossibleAuthorityServiceItem[],
    enforced: undefined as AuthorityService | undefined
  };
}

const config = {
  id: 'batchProcess',
  initial: 'idle',
  context: ({ input }) => ({
    documents: input.documents ?? [],
    signeeIssues: [],
    signed_documents: input.signed_documents ?? [],
    error: input.error ?? null,
    authority_service: input.authority_service,
    possible_authority_service: input.possible_authority_service ?? [],
    batch_sign_response: input.batch_sign_response,
    completed_time: input.completed_time
  }),
  states: {
    idle: {
      always: [
        { target: 'ongoing', guard: 'hasValidDocuments' },
        { target: 'error', actions: 'setError' }
      ]
    },

    ongoing: {
      type: 'parallel',
      states: {
        authService: {
          initial: 'idle',
          states: {
            idle: {
              on: { BEGIN_CHECKING: { target: 'checking' } }
            },
            checking: {
              invoke: {
                src: 'checkAuthorityServicesError',
                input: ({ context }) => ({
                  documents: context.documents,
                  authority_service: context.authority_service
                }),
                onDone: [
                  {
                    guard: ({ event }) =>
                      (event.output as CheckAuthActorOutput)?.next ===
                      'identify',
                    actions: ({ event, self }) => {
                      const out = event.output as CheckAuthActorOutput;
                      if (out?.unavailable)
                        self.send({
                          type: 'UNAVAILABLE_SERVICE'
                        } as BatchProcessEvent);
                    }
                  },
                  {
                    guard: ({ event }) =>
                      (event.output as CheckAuthActorOutput)?.next ===
                      'beginSigning',
                    actions: ({ self }) => {
                      self.send({ type: 'BEGIN_SIGNING' } as BatchProcessEvent);
                    }
                  }
                ],
                onError: {
                  target: '#batchProcess.ongoing.authService.error',
                  actions: 'setError'
                }
              },
              on: {
                BEGIN_SIGNING: { target: 'signing' },
                CANCEL_CHECKING: { target: 'idle' },
                UNAVAILABLE_SERVICE: { target: 'unavailable_service' }
              }
            },
            unavailable_service: {
              entry: { type: 'openIdentifyDialog' },
              on: { CONTINUE_SIGNING: { target: 'idle' } }
            },
            signing: {
              initial: 'ongoing',
              states: {
                ongoing: {
                  invoke: {
                    src: 'signBatchDocuments',
                    input: ({ context }) => ({
                      documents: context.documents,
                      authority_service: context.authority_service
                    }),
                    onDone: {
                      actions: ({ event, self }) => {
                        const out = event.output as SignBatchActorOutput;
                        const res = out?.batch_sign_response;
                        if (!res) return;

                        self.send({
                          type: 'SET_SIGN_RESPONSE',
                          batch_sign_response: res
                        } as BatchProcessEvent);

                        const snap = self.getSnapshot();
                        const hasCorrelation = !!res?.correlationCode;

                        if (snap.context.authority_service && hasCorrelation) {
                          self.send({ type: 'OPEN_PENDING' });
                        } else if (
                          snap.matches({
                            ongoing: { signatureMode: 'advanced' }
                          }) &&
                          snap.matches({
                            ongoing: { jurisdiction: 'zertes' }
                          }) &&
                          snap.context.authority_service ===
                            AuthorityService.MOBILE_ID
                        ) {
                          self.send({ type: 'OPEN_PENDING' });
                        }

                        self.send({ type: 'START_POLLING' });
                      }
                    },
                    onError: [
                      {
                        target: '#batchProcess.ongoing.authService.idle',
                        guard: 'isErrorRelatedWithAutograph',
                        actions: 'setErrorOnDocuments'
                      },
                      {
                        target: '#batchProcess.ongoing.authService.error',
                        actions: 'setError'
                      }
                    ]
                  },
                  on: {
                    SET_SIGN_RESPONSE: { actions: 'setBatchResponse' },
                    COMPLETE_SIGNING: [
                      {
                        target: 'completed',
                        guard: 'allDocumentsAreSigned',
                        actions: [
                          'updateSignedDocuments',
                          'setCompletedTime',
                          'setDefaultAuthorityService',
                          'persistDefaultAuthorityService'
                        ]
                      },
                      {
                        target: '#batchProcess.ongoing.authService.error',
                        actions: ['updateSignedDocuments', 'setError']
                      }
                    ],
                    OPEN_PENDING: { actions: 'openPendingDialog' },
                    START_POLLING: { actions: 'startAsyncPolling' }
                  }
                },
                completed: { type: 'final' }
              }
            },
            error: {
              on: { RETRY: { target: 'checking' } },
              entry: { type: 'setError' }
            }
          }
        },
        fileSize: {
          initial: 'idle',
          states: {
            idle: {
              always: [
                { target: 'tooLarge', guard: 'areFilesTooLarge' },
                { target: 'valid' }
              ]
            },
            tooLarge: {
              always: [{ target: 'valid', guard: 'areFilesSizeValid' }]
            },
            valid: { type: 'final' }
          }
        },
        fileAmount: {
          initial: 'idle',
          states: {
            idle: {
              always: [
                { target: 'exceeded', guard: 'areExceededFiles' },
                { target: 'tooMany', guard: 'areTooManyFiles' },
                { target: 'valid' }
              ]
            },
            valid: { type: 'final' },
            tooMany: { always: [{ target: 'idle', guard: 'areFilesCorrect' }] },
            exceeded: {
              always: [{ target: 'idle', guard: 'areFilesNotExceeded' }]
            }
          }
        },
        signatureMode: {
          initial: 'idle',
          states: {
            idle: {
              always: [
                { target: 'simple', guard: 'isSimpleSigning' },
                { target: 'advanced', guard: 'isAdvancedSigning' },
                { target: 'qualified' }
              ]
            },
            simple: {},
            advanced: {},
            qualified: {}
          }
        },
        jurisdiction: {
          initial: 'idle',
          states: {
            idle: {
              always: [
                { target: 'zertes', guard: 'isZertes' },
                { target: 'eidas', guard: 'isEidas' }
              ]
            },
            zertes: {},
            eidas: {}
          }
        },
        enforcedAuthorityService: {
          initial: 'idle',
          states: {
            idle: {
              always: [
                {
                  target: 'none',
                  guard: 'hasNoneAuthorityServices',
                  actions: 'setPossibleAuthorityServices'
                },
                {
                  target: 'matching',
                  guard: 'hasMatchingAuthorityServices',
                  actions: 'setPossibleAuthorityServices'
                },
                {
                  target: 'unmatching',
                  guard: 'hasUnmatchingAuthorityServices'
                }
              ]
            },
            none: {
              always: [
                { target: 'matching', guard: 'hasMatchingAuthorityServices' },
                {
                  target: 'unmatching',
                  guard: 'hasUnmatchingAuthorityServices'
                }
              ]
            },
            matching: {
              always: [
                { target: 'none', guard: 'hasNoneAuthorityServices' },
                {
                  target: 'unmatching',
                  guard: 'hasUnmatchingAuthorityServices'
                }
              ]
            },
            unmatching: {
              always: [
                { target: 'none', guard: 'hasNoneAuthorityServices' },
                { target: 'matching', guard: 'hasMatchingAuthorityServices' }
              ]
            }
          }
        },
        autograph: {
          initial: 'not_required',
          states: {
            not_required: {
              always: [
                { target: 'required', guard: 'areFilesWithAutographRequired' }
              ]
            },
            required: {
              always: [
                {
                  target: 'not_required',
                  guard: 'areFilesWithoutAutographRequired'
                }
              ]
            }
          }
        },
        autographPosition: {
          initial: 'not_required',
          states: {
            not_required: {
              always: [
                {
                  target: 'required',
                  guard: 'areFilesWithAutographPositionRequired'
                }
              ]
            },
            required: {
              always: [
                {
                  target: 'not_required',
                  guard: 'areFilesWithoutAutographPositionRequired'
                }
              ]
            }
          }
        }
      },
      on: {
        SELECT_AUTHORITY_SERVICE: { actions: 'setAuthorityService' }
      }
    },

    error: {
      on: { RETRY: { target: 'ongoing.authService.checking' } },
      entry: { type: 'setError' }
    }
  },
  on: {
    REMOVE_DOCUMENT: {
      actions: ['removeDocument', 'setPossibleAuthorityServices']
    }
  }
} as const;

export const batchProcessMachine = setup({
  types: {
    context: {} as BatchProcessContext,
    events: {} as BatchProcessEvent,
    input: {} as BatchProcessInput
  },
  actors: {
    checkAuthorityServicesError: fromPromise<
      CheckAuthActorOutput,
      CheckAuthActorInput
    >(async () => {
      throw new Error('checkAuthorityServicesError not provided');
    }),
    signBatchDocuments: fromPromise<SignBatchActorOutput, SignBatchActorInput>(
      async () => {
        throw new Error('signBatchDocuments not provided');
      }
    )
  },
  actions: {
    openIdentifyDialog: () => {},
    openPendingDialog: () => {},
    startAsyncPolling: () => {},
    persistDefaultAuthorityService: () => {},

    setErrorOnDocuments: assign({
      documents: ({ context, event }) => {
        const payload = getErrorPayload(event);
        const messageId = payload?.messageId;
        const documentId = payload?.documentId;
        if (!messageId || !documentId) return context.documents;

        if (messageId === 'error.sign.document.autograph.position.required') {
          return context.documents.map((doc) =>
            doc.documentId === documentId
              ? { ...doc, autographPositionRequired: true }
              : doc
          );
        }
        if (messageId === 'error.sign.document.autograph.required') {
          return context.documents.map((doc) =>
            doc.documentId === documentId
              ? { ...doc, autographRequired: true }
              : doc
          );
        }
        return context.documents;
      }
    }),

    setError: assign({
      error: ({ event }) => {
        const e = event;
        const signedDocs = e?.signed_documents as
          | Array<{ lastError?: { messageId?: string } }>
          | undefined;
        if (Array.isArray(signedDocs) && signedDocs.some((d) => d?.lastError)) {
          const first = signedDocs.find((d) => d?.lastError)?.lastError
            ?.messageId;
          if (
            first &&
            signedDocs.every((d) => d?.lastError?.messageId === first)
          )
            return { message: `batch.${first}` };
          return { message: 'batch.error.generic_error' };
        }

        const payload = getErrorPayload(event);
        if (payload?.messageId)
          return { message: `batch.${payload.messageId}` };

        return { message: 'batch.error.generic_error' };
      }
    }),

    removeDocument: assign({
      documents: ({ context, event }) => {
        if (event.type !== 'REMOVE_DOCUMENT') return context.documents;
        return context.documents.filter(
          (doc) => doc.documentId !== event.documentId
        );
      }
    }),

    setAuthorityService: assign({
      authority_service: ({ event, context }) => {
        if (event.type !== 'SELECT_AUTHORITY_SERVICE')
          return context.authority_service;
        return event.authority_service ?? AuthorityService.DEEP_ID;
      }
    }),

    updateSignedDocuments: assign({
      documents: ({ context, event }) => {
        if (event.type !== 'COMPLETE_SIGNING') return context.documents;
        const signed = event.signed_documents;
        return context.documents.map((document) => {
          const sd = signed.find((x) => x.documentId === document.documentId);
          if (!sd) return document;
          return {
            ...document,
            signStatus: sd.signStatus,
            lastError: sd.lastError,
            signeeId: sd.signeeId,
            documentStatus: sd.documentStatus
          };
        });
      }
    }),

    setBatchResponse: assign({
      batch_sign_response: ({ event, context }) => {
        if (event.type !== 'SET_SIGN_RESPONSE')
          return context.batch_sign_response;
        return event.batch_sign_response;
      }
    }),

    setCompletedTime: assign({
      completed_time: () => new Date().toISOString().replace('Z', '+00:00')
    }),

    setDefaultAuthorityService: assign({
      authority_service: ({ context }) =>
        context.authority_service ?? AuthorityService.DEEP_ID
    }),

    setPossibleAuthorityServices: assign({
      possible_authority_service: ({ context }) =>
        calcPossibleAuthorityServices(context.documents).possible,
      authority_service: ({ context }) => {
        const { enforced } = calcPossibleAuthorityServices(context.documents);
        return enforced ?? context.authority_service;
      }
    })
  },
  guards: {
    hasValidDocuments: ({ context }) => context.documents.length > 0,

    areFilesTooLarge: ({ context }) => {
      const total = context.documents.reduce(
        (acc, doc) => acc + (doc.initialSize || 0),
        0
      );
      return total > 40 * 1024 * 1024;
    },
    areFilesSizeValid: ({ context }) => {
      const total = context.documents.reduce(
        (acc, doc) => acc + (doc.initialSize || 0),
        0
      );
      return total <= 40 * 1024 * 1024;
    },

    areTooManyFiles: ({ context }) => context.documents.length >= 14,
    areExceededFiles: ({ context }) => context.documents.length > 100,
    areFilesNotExceeded: ({ context }) => context.documents.length <= 100,
    areFilesCorrect: ({ context }) => context.documents.length < 14,

    isSimpleSigning: ({ context }) =>
      context.documents.some(
        (d) => d.signatureMode === SignatureMode.TIMESTAMP
      ),
    isAdvancedSigning: ({ context }) =>
      context.documents.some((d) => d.signatureMode === SignatureMode.ADVANCED),

    isZertes: ({ context }) =>
      context.documents.some((d) => d.jurisdiction === Jurisdiction.ZERTES),
    isEidas: ({ context }) =>
      context.documents.some((d) => d.jurisdiction === Jurisdiction.EIDAS),

    allDocumentsAreSigned: ({ event }) => {
      if (event.type !== 'COMPLETE_SIGNING') return false;
      return event.signed_documents.every(
        (d) => d.signStatus !== SignStatus.PENDING
      );
    },

    hasNoneAuthorityServices: ({ context }) =>
      context.documents.every((doc) => !doc.requiredAuthorityService),

    hasMatchingAuthorityServices: ({ context }) => {
      const defined = context.documents
        .map((d) => d.requiredAuthorityService)
        .filter((s): s is AuthorityService => s != null);
      if (defined.length === 0) return false;
      return defined.every((s) => s === defined[0]);
    },

    hasUnmatchingAuthorityServices: ({ context }) => {
      const defined = context.documents
        .map((d) => d.requiredAuthorityService)
        .filter((s): s is AuthorityService => s != null);
      return new Set(defined).size > 1;
    },

    isErrorRelatedWithAutograph: ({ event }) => {
      const payload = getErrorPayload(event);
      const id = payload?.messageId;
      return (
        id === 'error.sign.document.autograph.position.required' ||
        id === 'error.sign.document.autograph.required'
      );
    },

    areFilesWithAutographRequired: ({ context }) =>
      context.documents.some((d) => !!d.autographRequired),
    areFilesWithoutAutographRequired: ({ context }) =>
      context.documents.every((d) => !d.autographRequired),
    areFilesWithAutographPositionRequired: ({ context }) =>
      context.documents.some((d) => !!d.autographPositionRequired),
    areFilesWithoutAutographPositionRequired: ({ context }) =>
      context.documents.every((d) => !d.autographPositionRequired)
  }
}).createMachine(config);
